import type { Game, TablesInsert, TablesUpdate } from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

/** Every game, archived ones included: Studio first, then by name. */
export function useGames() {
  return useQuery({
    queryKey: queryKeys.games,
    queryFn: async (): Promise<Game[]> => {
      const { data, error } = await supabase()
        .from('games')
        .select('*')
        .order('is_studio', { ascending: false })
        .order('name');
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateGame() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (game: Pick<TablesInsert<'games'>, 'name' | 'slug' | 'status' | 'pitch' | 'color'>) => {
      const { data, error } = await supabase().from('games').insert(game).select().single();
      if (error) throw error;
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.games }),
  });
}

export function useUpdateGame() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: { id: string } & Pick<
      TablesUpdate<'games'>,
      'name' | 'slug' | 'status' | 'pitch' | 'color' | 'github_repo' | 'archived_at'
    >) => {
      const { data, error } = await supabase().from('games').update(patch).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.games }),
  });
}
